import { createPinia } from 'pinia';
import { createApp, defineAsyncComponent, ref, onMounted, inject } from 'vue';

/////////////////////////////////////////////////////////////////////////////////////////////////// PLUGINS
import CookiesBar from './plugins/cookies-bar/index.js';
import Modals from './plugins/modals/index.js';
import Collapse from './plugins/collapse/index.js';
import Tabs from './plugins/tabs/index.js';
import ScrollTo from './plugins/scroll-to/index.js';
import SiteDrawer from './plugins/site-drawer/index.js';
import PageLoader from './plugins/page-loader/index.js';
import Animations from './plugins/animations/index.js';
import Notifications from './plugins/notifications/index.js';
import { createI18n } from 'vue-i18n';

//////////////////////////////////////////////////////////////////////////////////////// Shop
// Including Stripe on every page, allows to detect suspicious behavior
import Stripe from './plugins/stripe/index.js';
import Currency from './plugins/currency/index.js';

/////////////////////////////////////////////////////////////////////////////////////////////////// COMPONENTS
// import NewsletterInput from './components/newsletter-input/index.vue';
// const CircleText = defineAsyncComponent(() => import('./components/circle-text/index.vue'));
// const CollectionsMenu = defineAsyncComponent(() => import('./collections-menu.vue'));
// const RequestFormCraft = defineAsyncComponent(() => import('./request-form-craft.vue'));
// const RequestFormAmulets = defineAsyncComponent(() => import('./request-form-amulets.vue'));
// const GoogleMap = defineAsyncComponent(() => import('./components/google-map/index.vue'));

//////////////////////////////////////////////////////////////////////////////////////// Shop
import ShopMenu from './shop/shop-menu.vue';
const RelatedProducts = defineAsyncComponent(() => import('./shop/products/related-products.vue'));
import { SwiperSlide } from 'swiper/vue';
const SortDropdown = defineAsyncComponent(() => import('./sort-dropdown.vue'));
const ProductsList = defineAsyncComponent(() => import('./shop/products/products-list.vue'));
const ProductInner = defineAsyncComponent(() => import('./shop/products/product-inner.vue'));
const ProductPreview = defineAsyncComponent(() => import('./shop/products/product-preview.vue'));
import TheCart from './shop/cart/the-cart.vue';
const CheckoutDelivery = defineAsyncComponent(() => import('./shop/checkout/checkout-delivery.vue'));
const VoucherForm = defineAsyncComponent(() => import('./shop/checkout/voucher-form.vue'));
const ShippingMethods = defineAsyncComponent(() => import('./shop/checkout/shipping-methods.vue'));
const CheckoutPayment = defineAsyncComponent(() => import('./shop/checkout/checkout-payment.vue'));
const AccountIndex = defineAsyncComponent(() => import('./shop/account/index.vue'));
const AccountLogin = defineAsyncComponent(() => import('./shop/account/login.vue'));
const AccountRegister = defineAsyncComponent(() => import('./shop/account/register.vue'));
const AccountProfile = defineAsyncComponent(() => import('./shop/account/profile.vue'));
const AccountAddresses = defineAsyncComponent(() => import('./shop/account/addresses.vue'));
const AccountPassword = defineAsyncComponent(() => import('./shop/account/password.vue')); // simply updates their password once they know / remember their current whence loggedin
const AccountResetPassword = defineAsyncComponent(() => import('./shop/account/reset-password.vue')); // it's the form that changes their password once they asked for a reset-password-link when they forgot it
const AccountOrders = defineAsyncComponent(() => import('./shop/account/orders.vue'));
const AccountWishlist = defineAsyncComponent(() => import('./shop/account/wishlist.vue'));

/////////////////////////////////////////////////////////////////////////////////////////////////// APP
import { viewportSize, devices } from './imports/utils';
import { useWishlistStore } from './shop/account/wishlist-store.js';

const i18n = createI18n({
    legacy: false,
    locale: window.Laravel.locale,
    messages: window.Laravel.translations,
});

window.app = createApp({
    components: {
        // CircleText,
        // CollectionsMenu,
        // NewsletterInput,
        // RequestFormCraft,
        // RequestFormAmulets,
        SortDropdown,
        RelatedProducts,
        SwiperSlide,
        /////////////////////////////////////////////////////////////////////////////////////////////////// SHOP
        ShopMenu,
        TheCart,
        ProductsList,
        ProductInner,
        ProductPreview,
        CheckoutDelivery,
        VoucherForm,
        ShippingMethods,
        CheckoutPayment,
        AccountIndex,
        AccountLogin,
        AccountRegister,
        AccountProfile,
        AccountPassword,
        AccountResetPassword,
        AccountAddresses,
        AccountOrders,
        AccountWishlist
    },
    setup() {
        if (process.env.NODE_ENV === 'production') console.log('%c Coded By Freshdesign Team! ', 'background: #000; color: #c9ec01; padding: 10px; font-size: 13px;');

        ///////////////////////////////////////////////////////////////////////////// scrollTop
        const scrollTop = ref(0);
        const onScroll = () => {
            scrollTop.value = window.scrollY;
        }
        window.addEventListener("scroll", onScroll, { passive: true });

        ///////////////////////////////////////////////////////////////////////////// viewportSize
        const viewport = ref(viewportSize());
        const onResize = () => {
            viewport.value = viewportSize();
        }
        window.addEventListener("resize", onResize, { passive: true });

        onMounted(() => {
            if (devices()) {
                document.getElementsByTagName('html')[0].classList.add('touch-device');
            }
        });

        ///////////////////////////////////////////////////////////////////////////// Tabs
        const $tabs = inject('$tabs');
        
        onMounted(() => {
            $tabs.init();
        });

        ///////////////////////////////////////////////////////////////////////////// Animations
        const $animations = inject('$animations');

        onMounted(() => {
            // $animations.menu.init();
            // $animations.blocks.init();
            $animations.parallax.init();
            // $animations.counter.init();
            $animations.followMouse.init();
        });

        ///////////////////////////////////////////////////////////////////////////// Teleport fix
        const mounted = ref(false);
        onMounted(() => {
            mounted.value = true;
        });

        // fetch wishlist if present
        const wishlistStoreGlobal = useWishlistStore()
        wishlistStoreGlobal.get();

        ///////////////////////////////////////////////////////////////////////////// return
        return {
            // accountStore: useAccountStore(), // if you want it accessible in blades 
            assets: `/public/site/${process.env.MIX_TEMPLATE_FOLDER}/dist`,
            mounted,
            wishlistStoreGlobal,
            shopUrls: window.Laravel.shop,
            scrollTop,
            viewport
        }
    },
    // destroyed() {
    //     // window.removeEventListener("resize", this.onResize)
    // }
})
    .component('BgImage', defineAsyncComponent(() => import('./components/bg-image/index.vue')))
    .component('BgVideo', defineAsyncComponent(() => import('./components/bg-video/index.vue')))
    .use(createPinia())
    .use(i18n)
    .use(CookiesBar)
    .use(Notifications)
    .use(Modals)
    .use(Collapse)
    .use(Tabs)
    .use(PageLoader)
    .use(ScrollTo, {
        // debug: true,
        headerHeight: 155,
    })
    .use(SiteDrawer)
    .use(Stripe, {
        publicKey: window?.Laravel?.shop?.stripe?.public_key || '',
        mode: window?.Laravel?.shop?.stripe?.mode || '',
    })
    .use(Currency)
    .use(Animations)
    .mount('#app')